import { useEffect, useId } from 'react';

// Imported from the leaf module rather than the `@/lib/mobile` barrel: the
// barrel re-exports the biometric and push plugins, and pulling those into a
// component test that has no reason to mock them fails at module resolution.
import { claimStatusBarSurface, releaseStatusBarSurface } from '@/lib/mobile/status-bar';

import type { StatusBarSurface } from '@/lib/mobile/status-bar';

/**
 * Claim the native status bar for as long as this component is mounted.
 *
 * Mount claims, unmount releases and repaints for whatever was underneath.
 * That last part is the whole reason this is a hook rather than a bare
 * `applyStatusBarSurface` call in an effect: an overlay that paints the bar on
 * open and does nothing on close leaves the wrong ink behind, and it cannot fix
 * that itself because it has no idea what it covered.
 *
 * A no-op off native - the underlying module guards for it - so components can
 * use it unconditionally and Storybook stays quiet.
 */
export function useStatusBarSurface(surface: StatusBarSurface): void {
  // Identifies the claim across re-renders, so a surface change updates it in
  // place instead of stacking a second claim that never gets released.
  const id = useId();

  useEffect(() => {
    claimStatusBarSurface(id, surface);
    return () => {
      releaseStatusBarSurface(id);
    };
  }, [id, surface]);
}
